import React, { useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { ArrowDown, ArrowUp, CheckSquare, Loader2, Save, Square } from 'lucide-react';

const Fila = ({ col, activa, i, total, onToggle, onMover }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-md border ${activa ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`}
    data-testid={`expmov-config-col-${col.key}`}>
    <button type="button" onClick={onToggle} className="flex items-center gap-2 flex-1 text-left text-sm" data-testid={`expmov-config-toggle-${col.key}`}>
      {activa ? <CheckSquare className="h-4 w-4 text-blue-700" /> : <Square className="h-4 w-4 text-slate-400" />}
      <span className={activa ? 'text-slate-900 font-medium' : 'text-slate-500'}>{col.label}</span>
    </button>
    {activa && (
      <>
        <button type="button" disabled={i === 0} onClick={() => onMover(-1)} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" data-testid={`expmov-config-subir-${col.key}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button type="button" disabled={i === total - 1} onClick={() => onMover(1)} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" data-testid={`expmov-config-bajar-${col.key}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      </>
    )}
  </div>
);

// Solo admin: columnas (y orden) y hoja Resumen que usan todos en Excel, PDF y vista previa.
export const ConfigExportacion = ({ config, onGuardado }) => {
  const [cols, setCols] = useState(config.diseno?.columnas || []);
  const [resumen, setResumen] = useState(!!config.diseno?.hoja_resumen);
  const [guardando, setGuardando] = useState(false);
  const porKey = Object.fromEntries((config.catalogo || []).map((c) => [c.key, c]));
  const inactivas = (config.catalogo || []).filter((c) => !cols.includes(c.key));

  const mover = (i, d) => setCols((c) => { const n = [...c]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put('/export-movimientos/config', { columnas: cols, hoja_resumen: resumen });
      toast.success('Configuración guardada: se usa en los próximos pedidos');
      onGuardado(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  return (
    <Card className="p-4 sm:p-5 space-y-4 border-slate-200" data-testid="expmov-config">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Qué se exporta</h2>
        <p className="text-xs text-slate-500">Vale para todos los usuarios, en el Excel, el PDF y la vista previa. Los pedidos ya generados no cambian.</p>
      </div>
      <label className="flex items-center gap-3 text-sm" data-testid="expmov-config-resumen">
        <Switch checked={resumen} onCheckedChange={setResumen} data-testid="expmov-config-resumen-switch" />
        Incluir la hoja Resumen en el Excel (datos del pedido y totales)
      </label>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-slate-500 uppercase">Columnas que se exportan, en este orden ({cols.length})</p>
          {cols.map((k, i) => porKey[k] && (
            <Fila key={k} col={porKey[k]} activa i={i} total={cols.length}
              onToggle={() => setCols((c) => c.filter((x) => x !== k))} onMover={(d) => mover(i, d)} />
          ))}
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-slate-500 uppercase">No se exportan</p>
          {inactivas.map((c) => <Fila key={c.key} col={c} onToggle={() => setCols((x) => [...x, c.key])} />)}
          {inactivas.length === 0 && <p className="text-xs text-slate-400">Están todas las columnas.</p>}
        </div>
      </div>
      <Button onClick={guardar} disabled={guardando || cols.length === 0} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="expmov-config-guardar">
        {guardando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar
      </Button>
    </Card>
  );
};

export default ConfigExportacion;
